import { GetServerSideProps } from "next"
import { findResultsState } from "react-instantsearch-dom/server"
import qs from "qs"
import { InstantSearchProps } from "react-instantsearch-core"

import { AppProviders } from "../_app"
import CategoryPageContainer, { Props } from "containers/CategoryPage/CategoryPageContainer"
import SEO from "components/SEO"

import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchCategory, {
	fetchCategoryIdBySlug,
} from "utils/commerceTools/graphql/queries/fetchCategory"
import searchClient from "utils/algolia/searchClient"
import { urlToSearchState } from "@secureo/common/hooks/useAlgoliaURLSync"
import getValueFromLocalizedStringWithFallback from "utils/i18n/getValueFromLocalizedStringWithFallback"
import {
	getLocalizedMetaTitle,
	getLocalizedMetaDescription,
} from "utils/i18n/getLocalizedMetaAttributeValue"
import normalizeLocale from "@secureo/common/utils/normalizeLocale"
import serializeJSON from "@secureo/common/utils/serializeJSON"
import { supportedValidLocales } from "utils/i18n/supportedLocalesAndCurrencyCodes"
import { getServerTranslations } from "utils/i18n/getServerTranslations"

import { Category } from "typings/Category"
import { useContext } from "react"
import AppContext from "@secureo/common/context/AppContext/AppContext"
import { getMetaDescriptionFallback, getMetaTitleFallback } from "pages/pc/[slug]"

const CategoryPage = (props: Props) => {
	const { locale, category, language } = props
	const { currencyCode } = useContext(AppContext)

	const name = getValueFromLocalizedStringWithFallback(category?.nameAllLocales, locale)

	const metaTitle =
		getLocalizedMetaTitle(category, locale) || getMetaTitleFallback(name, locale, language)
	const metaDescription =
		getLocalizedMetaDescription(category, locale) ||
		getMetaDescriptionFallback(name, locale, language, currencyCode)

	return (
		<>
			<SEO
				asPath={`/c/${category.slug}`}
				pageType={"category"}
				title={metaTitle}
				description={metaDescription}
			/>
			<CategoryPageContainer {...props} />
		</>
	)
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const { req, res, params, locale: rawLocale } = context

	const slug = params?.slug as string
	const { locale, language, countryCode } = normalizeLocale(rawLocale)

	if (!supportedValidLocales.includes(locale)) {
		res.statusCode = 404
		return {
			notFound: true,
			props: {
				statusCode: 404,
				error: "Locale not supported",
			},
		}
	}

	const accessToken = await anonymousAccessTokenCache()
	const category = await fetchCategory(accessToken, language, slug)
	if (!category) {
		res.statusCode = 404
		return {
			notFound: true,
			props: {
				statusCode: 404,
				error: "Category not found",
			},
		}
	}

	const customFieldsRaw = category.custom?.customFieldsRaw ?? []
	let referencedCategoryId = category.id
	let referencedCategory: Category = null
	let parsedInitialFilterValues: qs.ParsedQs = {}

	const isFilterCategory = customFieldsRaw?.find(({ name }) => name === "isFilterCategory")?.value
	const referencedCategoryKey = customFieldsRaw?.find(
		({ name }) => name === "referencedCategoryKey",
	)?.value
	const refinementList = customFieldsRaw?.find(({ name }) => name === "refinementList")?.value

	// Fetch initial resultState from Algolia
	const unsortedIndexName = `${locale}-products`
	let indexName = unsortedIndexName
	const searchState = urlToSearchState(req.url)
	let resultsState: InstantSearchProps["resultsState"] = {}

	// Landing page categories
	if (isFilterCategory && referencedCategoryKey && refinementList) {
		// Fetch id of referenced category
		referencedCategory = await fetchCategoryIdBySlug(
			accessToken,
			language,
			referencedCategoryKey,
		)
		referencedCategoryId = referencedCategory?.id
		parsedInitialFilterValues = qs.parse(refinementList)
		const { sortBy } = parsedInitialFilterValues

		if (sortBy) {
			const nonLocalizedIndexName = (sortBy as string).split("-")?.[2] ?? "products"
			indexName = `${locale}-${nonLocalizedIndexName}`
		}
	}

	const localizationProps = await getServerTranslations(locale, [
		"common",
		"countries",
		"languages",
		"category",
	])

	try {
		resultsState = await findResultsState(
			(props) => (
				<AppProviders>
					<CategoryPageContainer
						category={category}
						referencedCategoryId={referencedCategoryId}
						referencedCategory={referencedCategory}
						parsedInitialFilterValues={parsedInitialFilterValues}
						initialSearchState={searchState}
						locale={locale}
						language={language}
						indexName={indexName}
						unsortedIndexName={unsortedIndexName}
						{...localizationProps}
						{...props}
					/>
				</AppProviders>
			),
			{
				indexName,
				searchClient,
				searchState,
			},
		)
	} catch (err) {
		console.error(err)
		res.statusCode = 500
		return {
			props: {
				statusCode: 500,
				error: "Failed to create resultsState",
			},
		}
	}

	const props: Props = {
		category,
		referencedCategoryId,
		referencedCategory,
		parsedInitialFilterValues,
		indexName,
		unsortedIndexName,
		resultsState,
		initialSearchState: searchState,
		locale,
		language,
		countryCode,
		...localizationProps,
	}

	const oneHoursInSeconds = 60 * 60

	res.setHeader(
		"Cache-Control",
		`s-maxage=30, stale-while-revalidate=${oneHoursInSeconds * 12 - 30}`,
	)

	return {
		props: serializeJSON(props),
	}
}

export default CategoryPage
